import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { BookOpen, Search, HandHeart, Check, Sparkles, ArrowLeft } from "lucide-react";

export default function Psalms() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [psalms, setPsalms] = useState([]);
    const [themes, setThemes] = useState({});
    const [today, setToday] = useState(null);
    const [q, setQ] = useState("");
    const [theme, setTheme] = useState("all");
    const [selected, setSelected] = useState(null);
    const [prayedId, setPrayedId] = useState(null);
    const [counts, setCounts] = useState({});

    useEffect(() => {
        api.get("/psalms").then(({ data }) => {
            setPsalms(data.psalms || []);
            setThemes(data.themes || {});
        }).catch(() => {});
        api.get("/psalms/today").then(({ data }) => setToday(data)).catch(() => {});
    }, []);

    const filtered = useMemo(() => {
        const ql = q.trim().toLowerCase();
        return psalms.filter((p) => {
            if (theme !== "all" && p.theme !== theme) return false;
            if (!ql) return true;
            return (
                String(p.n) === ql ||
                p.title.toLowerCase().includes(ql) ||
                p.verse.toLowerCase().includes(ql)
            );
        });
    }, [psalms, q, theme]);

    const pray = async (number) => {
        if (!user?.id) { window.location.href = "/login"; return; }
        try {
            const { data } = await api.post(`/psalms/${number}/pray`);
            setPrayedId(number);
            setCounts((c) => ({ ...c, [number]: data.pray_count }));
            setTimeout(() => setPrayedId(null), 2000);
        } catch { /* ignore */ }
    };

    if (selected) {
        return (
            <div className="parchment min-h-[calc(100vh-4rem)]">
                <div className="mx-auto max-w-3xl px-6 md:px-10 py-12">
                    <button onClick={() => setSelected(null)} className="inline-flex items-center gap-1 text-sm mb-6" style={{ color: "#7F1D1D" }} data-testid="psalms-back">
                        <ArrowLeft size={14} /> {t("psalms.back")}
                    </button>
                    <div className="text-xs tracking-[0.28em] uppercase" style={{ color: "#7F1D1D" }}>
                        Psalm {selected.n}
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl mt-2" style={{ color: "#1E1B4B" }}>{selected.title}</h1>
                    <div className="mt-2 text-xs tracking-[0.2em] uppercase" style={{ color: "#525252" }}>
                        {themes[selected.theme] || selected.theme}
                    </div>
                    <div className="gold-divider my-8" />
                    <blockquote className="font-display text-2xl sm:text-3xl leading-relaxed" style={{ color: "#1E1B4B" }}>
                        “{selected.verse}”
                    </blockquote>
                    <div className="mt-8 flex items-center gap-3 flex-wrap">
                        <button onClick={() => pray(selected.n)} className="btn-primary inline-flex items-center gap-2" data-testid={`psalms-pray-${selected.n}`}>
                            {prayedId === selected.n ? <><Check size={16} /> {t("psalms.prayed")} {selected.n}</> : <><HandHeart size={16} /> {t("psalms.pray")}</>}
                        </button>
                        {counts[selected.n] != null && (
                            <span className="text-sm" style={{ color: "#525252" }}>{counts[selected.n]} {t("psalms.count")}</span>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="parchment min-h-[calc(100vh-4rem)]">
            <div className="mx-auto max-w-7xl px-6 md:px-10 py-12">
                <div className="text-xs tracking-[0.28em] uppercase flex items-center gap-2" style={{ color: "#7F1D1D" }}>
                    <BookOpen size={14} /> {t("psalms.kicker")}
                </div>
                <h1 className="font-display text-4xl sm:text-5xl mt-2" style={{ color: "#1E1B4B" }}>{t("psalms.h")}</h1>
                <p className="mt-3 max-w-2xl" style={{ color: "#525252" }}>{t("psalms.sub")}</p>

                {today && (
                    <div className="mt-8 p-card p-6 sm:p-8 relative overflow-hidden" data-testid="psalms-today">
                        <div className="absolute top-4 right-4">
                            <Sparkles size={18} style={{ color: "#D4AF37" }} />
                        </div>
                        <div className="text-xs tracking-[0.2em] uppercase" style={{ color: "#7F1D1D" }}>{t("psalms.today")}</div>
                        <div className="font-display text-3xl mt-1" style={{ color: "#1E1B4B" }}>
                            Psalm {today.n} — {today.title}
                        </div>
                        <blockquote className="mt-3 font-display text-xl leading-relaxed" style={{ color: "#525252" }}>
                            “{today.verse}”
                        </blockquote>
                        <div className="mt-4 flex gap-3 flex-wrap">
                            <button onClick={() => setSelected(today)} className="btn-primary text-sm" data-testid="psalms-today-open">Read & Pray</button>
                            <button onClick={() => pray(today.n)} className="btn-gold text-sm inline-flex items-center gap-1" data-testid={`psalms-today-pray-${today.n}`}>
                                <HandHeart size={14} /> {t("psalms.pray")}
                            </button>
                        </div>
                    </div>
                )}

                <div className="mt-10 flex items-end justify-between flex-wrap gap-4">
                    <div className="font-display text-3xl" style={{ color: "#1E1B4B" }}>{t("psalms.all")}</div>
                    <div className="relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#7F1D1D" }} />
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder={t("psalms.search")}
                            className="pl-9 pr-4 py-2.5 rounded-full border text-sm w-72"
                            style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                            data-testid="psalms-search"
                        />
                    </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-2" data-testid="psalms-theme-chips">
                    <button className={`chip ${theme === "all" ? "active" : ""}`} onClick={() => setTheme("all")} data-testid="psalms-theme-all">All</button>
                    {Object.entries(themes).map(([k, l]) => (
                        <button key={k} className={`chip ${theme === k ? "active" : ""}`} onClick={() => setTheme(k)} data-testid={`psalms-theme-${k}`}>{l}</button>
                    ))}
                </div>

                <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filtered.length === 0 && (
                        <div className="col-span-full text-sm" style={{ color: "#525252" }}>No Psalms match your search.</div>
                    )}
                    {filtered.map((p) => (
                        <button
                            key={p.n}
                            onClick={() => setSelected(p)}
                            className="p-card p-5 text-left hover:-translate-y-0.5 transition-transform"
                            data-testid={`psalms-card-${p.n}`}
                        >
                            <div className="flex items-center justify-between">
                                <span className="font-display text-2xl" style={{ color: "#D4AF37" }}>{p.n}</span>
                                <span className="text-[10px] tracking-[0.2em] uppercase px-2 py-0.5 rounded-full" style={{ background: "#F5F0E6", color: "#7F1D1D" }}>
                                    {themes[p.theme] || p.theme}
                                </span>
                            </div>
                            <div className="font-display text-xl mt-2" style={{ color: "#1E1B4B" }}>{p.title}</div>
                            <div className="text-sm mt-2 line-clamp-3" style={{ color: "#525252" }}>{p.verse}</div>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}
